import React, { useEffect, useState, useRef } from 'react'
import { withRouter } from 'react-router-dom'
import './Breadcrumb.css'
import Tooltip from '../Tooltip/Tooltip'
import Icon from '../Icon2'
import { Truncate } from '../Truncate/Truncate'
import { useResizeObserver } from '../Truncate/use-resize-observer'
import { Heap } from '../../utils/index'
import Menu from '../Menu/Menu'
import { isEmpty, isEqual } from 'lodash'

interface IBreadcrumbItem {
  name: React.ReactNode
  action?: (breadcrumbItem) => void
  path: string
}

interface IProps {
  maxWidth?: number
  tooltipPosition?: string
  breadcrumbItems: Array<IBreadcrumbItem>
  testId?: string
  truncateLimit?: number,
  isResponsive?: boolean,
  version?: 'v1'
}

interface EllipsisProps {
  ellipsisIndex: number
  onDropdownClick?: () => void
}

interface IconProps {
  version: 'v1' | 'v2',
  name: string,
}
interface IPropsv2 {
  maxWidth?: number;
  breadcrumbItems: Array<IBreadcrumbItemv2>;
  testId?: string;
  ellipsis?: EllipsisProps;
  breadcrumbLimit?: number;
  version?: 'v2';
  isResponsive?: 'ellipse' | 'truncate' | 'ellipseFromLast';
  presentLocation?: string;
  maxChar?: number;
}


export interface IBreadcrumbItemv2 {
  icon?: IconProps
  name: string
  action?: () => void
  onDropdownClick?: () => void
  unLinkPresentItem?: Boolean,
  dropdownItems?: any[]
}

const setEllipsisIndex = (breadcrumbLength: number, ellipsisIndex: number, breadcrumbLimit?: number) => {
  if (breadcrumbLimit)
    breadcrumbLength = Math.min(breadcrumbLength, breadcrumbLimit)

  if (ellipsisIndex > breadcrumbLength - 2)
    return breadcrumbLength - 2;

  if (ellipsisIndex < 1)
    return 1;

  return ellipsisIndex;
}

interface IHeapItem {
  ref: HTMLDivElement | null;
  width: number
  index?: number
}


const Breadcrumbv2 = (props: IPropsv2) => {
  const { testId = 'cs-breadcrumb', breadcrumbItems, ellipsis, maxWidth, isResponsive, presentLocation, maxChar, version = "v2" } = props;
  const breadcrumbLength = presentLocation ? breadcrumbItems.length : breadcrumbItems.length + 1;

  const breadcrumbLimit = props.breadcrumbLimit || breadcrumbLength;
  const ellipsisDropdown = useRef(null);
  const breadcrumbWrapperRef = useRef<(HTMLDivElement | null)>(null);
  const breadcrumbInnerRef = useRef<(HTMLDivElement | null)>(null);
  const breadcrumbLinkRef = useRef<(HTMLDivElement | null)[]>([]);
  const ellipsisRef = useRef(null);


  const [showEllipsisDropdown, setShowEllipsisDropdown] = useState(false);
  const [initialWidths, setIntialWidths] = useState([]);
  const [initialTotalWidth, setInitialTotalWidth] = useState(0);
  const [startEllipseIndex, setStartEllipseIndex] = useState(0);

  const [hiddenItems, setHiddenItems] = useState([]);

  const [hasEllipsis, setHasEllipsis] = useState(isResponsive && breadcrumbLength > 2 && (isResponsive === 'truncate' ? (breadcrumbLength > breadcrumbLimit) : false) || (isResponsive === 'ellipse' && initialTotalWidth && breadcrumbInnerRef?.current ? (breadcrumbWrapperRef?.current?.offsetWidth < initialTotalWidth) : false));

  let ellipsisItems = [];
  const isEllipseResponsive = isResponsive === 'ellipse';
  const calculatedEllipsisIndex = Math.floor(breadcrumbLength / 2);
  const ellipsisIndex = (isEllipseResponsive ? calculatedEllipsisIndex : setEllipsisIndex(breadcrumbLength, ellipsis?.ellipsisIndex, breadcrumbLimit));


  const toggleEllipsisDropdown = (e) => {
    e.stopPropagation();
    setShowEllipsisDropdown(!showEllipsisDropdown);
  }

  let dimensions;
  if (isResponsive)
    dimensions = useResizeObserver(breadcrumbWrapperRef);

  // useEffect to calculate initialWidths
  useEffect(() => {
    if (isResponsive) {
      let animationFrameId: number;
      const getInitialWidths = () => {
        const initialWidths = [];
        for (let i = 0; i < breadcrumbLength; i++) {
          if (breadcrumbLinkRef?.current[i])
            initialWidths.push(breadcrumbLinkRef?.current[i]?.offsetWidth);
          else
            initialWidths.push(0);
        }
        setIntialWidths(initialWidths);
        setInitialTotalWidth(breadcrumbInnerRef?.current?.offsetWidth);
      }
      getInitialWidths();
      animationFrameId = requestAnimationFrame(() => getInitialWidths());
      return () => {
        cancelAnimationFrame(animationFrameId);
      };
    }
  }, [breadcrumbLength])

  useEffect(() => {
    if (isResponsive === 'ellipseFromLast') {
      ellipsisItems = [];
      let overflow = breadcrumbInnerRef?.current?.offsetWidth - breadcrumbWrapperRef?.current?.offsetWidth + 200;      
      if (overflow > 0) {
        for (let i = breadcrumbLinkRef?.current?.length - 2; i >= 0; i--) {
          const link = breadcrumbLinkRef?.current[i];
          if (link && !link.classList.contains('isHidden') && !link.classList.contains('Breadcrumb--v2__unlink-last-item')) {
            if (overflow-link.offsetWidth <= 0) {
              break;
            }
            overflow -= link.offsetWidth;
            link.classList.add('isHidden');
          }
        }
        setHasEllipsis(true);
      } else {
        for (let i = breadcrumbLinkRef?.current?.length - 2; i < 0; i++) {
          const link = breadcrumbLinkRef.current[i];
          if (link && link.classList.contains('isHidden')) {
            if (overflow+link.offsetWidth > 0) {
              break;
            }
            overflow += link.offsetWidth;
            link.classList.remove('isHidden');
          }
        }
        if (overflow <= 0) {
          setHasEllipsis(false);
        }
      }
      if (overflow > 0) {
        for (let i=0; i < breadcrumbLinkRef?.current?.length - 1; i++) {
          if (breadcrumbLinkRef?.current[i] && breadcrumbLinkRef?.current[i].classList.contains('isHidden')) {
            setHiddenItems(prev => [...prev, i]);
          } 
        }
      } else {
        setHiddenItems([]);
      }
    }
  }, [dimensions, hasEllipsis, breadcrumbLength])

  // useEffect for isResponsive === 'ellipse'
  useEffect(() => {
    if (isResponsive === 'ellipse') {

      let animationFrameId: number;

      const handleResizing = () => {
        if (initialTotalWidth && breadcrumbInnerRef && initialTotalWidth <= breadcrumbWrapperRef.current.offsetWidth) {
          if (hasEllipsis) {
            for (let i = 0; i != breadcrumbLength; i++) {
              if (breadcrumbLinkRef.current[i] && breadcrumbLinkRef.current[i].classList.contains('isHidden')) {
                breadcrumbLinkRef.current[i]?.classList.remove("isHidden");
              }
            }
            setHasEllipsis(false)
          }
          return;
        }

        // Condition to convert non-ellipsis to ellipsis
        if (!hasEllipsis && initialTotalWidth && breadcrumbWrapperRef?.current && breadcrumbWrapperRef.current.offsetWidth < initialTotalWidth) {
          setHasEllipsis(true);
        }

        const EllipseArray = [];

        let p1 = ellipsisIndex - 1;
        let p2 = ellipsisIndex + 1;

        while (p1 >= 1 && p2 < breadcrumbLength - 1) {
          EllipseArray.push(p1--);
          EllipseArray.push(p2++);
        }

        while (p1 >= 1)
          EllipseArray.push(p1--);

        while (p2 < breadcrumbLength - 1)
          EllipseArray.push(p2++);


        let offset = breadcrumbWrapperRef?.current?.offsetWidth - breadcrumbInnerRef?.current?.offsetWidth;

        if (offset < 0) {

          const heap = []

          for (const num of EllipseArray) {
            if (breadcrumbLinkRef.current[num] && !breadcrumbLinkRef.current[num].classList.contains('isHidden')) {
              heap.push({ ref: breadcrumbLinkRef.current[num], index: num })
            }
          }

          // Condition of shrinking
          for (const top of heap) {
            if (top && top.index !== ellipsisIndex) {
              offset += top.ref.offsetWidth;
              top.ref.classList.add('isHidden');
              ellipsisItems.push(top.index);
            }

            if (offset >= 0)
              break;
          }

        } else if (offset > 0) {
          const arr = [];

          for (let num = EllipseArray.length - 1; num >= 0; num--) {
            if (breadcrumbLinkRef?.current[EllipseArray[num]] && breadcrumbLinkRef?.current[EllipseArray[num]]?.classList?.contains('isHidden')) {
              arr.push({ ref: breadcrumbLinkRef.current[EllipseArray[num]], width: initialWidths[EllipseArray[num]], index: EllipseArray[num] })
            }
          }

          arr.push({ ref: breadcrumbLinkRef?.current[EllipseArray[ellipsisIndex]], width: initialWidths[ellipsisIndex], index: EllipseArray[ellipsisIndex] })

          for (const top of arr) {
            if (top.width + breadcrumbInnerRef?.current?.offsetWidth <= breadcrumbWrapperRef?.current?.offsetWidth) {
              offset += top.width;
              top.ref.classList.remove('isHidden');
              ellipsisItems = ellipsisItems.filter((el) => el !== top.index);
            } else
              break;

            if (offset <= 0)
              break;
          }
        }

      };

      animationFrameId = requestAnimationFrame(handleResizing);

      return () => {
        cancelAnimationFrame(animationFrameId);
      };
    }
  }, [dimensions, hasEllipsis, breadcrumbLength]);

  // useEffect for isResponsive === 'truncate'
  useEffect(() => {
    if (isResponsive == 'truncate') {
      let animationFrameId: number;
      const handleResizing = (initialWidths: any[], initialTotalWidth: number) => {

        if (!breadcrumbInnerRef.current || !breadcrumbWrapperRef.current)
          return;

        if (breadcrumbWrapperRef?.current?.offsetWidth < breadcrumbInnerRef?.current?.offsetWidth) {
          let offset = breadcrumbInnerRef?.current?.offsetWidth - breadcrumbWrapperRef?.current?.offsetWidth;
          // change the breadcrumbLinkRef to ellipsis such that breadcrumbWrapperRef.current.offsetWidth < breadcrumbLinkRef.current.offsetWidth
          // distribute the offset among maxBreadcrumbLimit -1 floor 
          const offsetPerItem = hasEllipsis ? Math.ceil(offset * 1.0 / (breadcrumbLimit - 1)) / 2 : Math.ceil(offset * 1.0 / (breadcrumbLimit)) / 2;

          // max heap to store ref and width of each breadcrumbLinkRef
          const maxHeap = new Heap<IHeapItem>((a, b) => a.width - b.width);

          // push the ref and width of each breadcrumbLinkRef
          for (let i = 0; i < breadcrumbLength; i++) {
            if (breadcrumbLinkRef.current[i])
              maxHeap.push({ ref: breadcrumbLinkRef.current[i], width: parseInt(breadcrumbLinkRef.current[i].style.width) || breadcrumbLinkRef.current[i].offsetWidth });
          }
          while (offset > 0 && !maxHeap.isEmpty()) {
            const maxItem = maxHeap.pop();

            if (maxItem && maxItem.ref) {
              const newWidth = maxItem.width - offsetPerItem;
              maxItem.ref.style.width = `${newWidth}px`;
              offset -= offsetPerItem;
              maxHeap.push({ ref: maxItem.ref, width: newWidth });
            }
          }
        } else {

          if (initialTotalWidth === 0) return;

          if (breadcrumbWrapperRef.current.offsetWidth > initialTotalWidth) {
            for (let i = 0; i < breadcrumbLength; i++) {
              if (breadcrumbLinkRef.current[i]) breadcrumbLinkRef.current[i].style.width = ``;
            }
            return;
          }

          let offset = breadcrumbWrapperRef.current.offsetWidth - breadcrumbInnerRef.current.offsetWidth;

          const breadcrumbArray = [];

          // Collect items that need expansion
          for (let i = 0; i < breadcrumbLength; i++) {
            if (breadcrumbLinkRef.current[i] && breadcrumbLinkRef.current[i].offsetWidth < initialWidths[i]) {
              breadcrumbArray.push({
                ref: breadcrumbLinkRef.current[i],
                width: breadcrumbLinkRef.current[i].offsetWidth,
                initialWidth: initialWidths[i],
              });

            }
          }

          let totalItems = breadcrumbArray.length;
          let offsetPerItem = Math.ceil(offset / totalItems);

          while (offset > 0 && totalItems > 0 && breadcrumbArray.length > 0) {
            for (let i = 0; i < breadcrumbArray.length; i++) {
              if (offset <= 0) break;

              const { ref, width, initialWidth } = breadcrumbArray[i];

              if (width < initialWidth) {
                const newWidth = width + offsetPerItem;

                // Cap the width at initialWidth
                if (newWidth >= initialWidth) {
                  ref.style.width = ``;
                  offset -= (initialWidth - width);
                } else {
                  ref.style.width = `${newWidth}px`;
                  offset -= offsetPerItem;
                  breadcrumbArray[i].width = newWidth; // Update the stored width
                }

                // Remove items that have reached their maximum width
                if (breadcrumbArray[i].width >= initialWidth) {
                  breadcrumbArray.splice(i, 1);
                  i--;
                }
              }
            }

            // Recalculate the offset per item if the list changes
            totalItems = breadcrumbArray.length;
            if (totalItems > 0) {
              offsetPerItem = Math.ceil(offset / totalItems);
            }
          }
        }
      };

      animationFrameId = requestAnimationFrame(() => handleResizing(initialWidths, initialTotalWidth));
      return () => {
        cancelAnimationFrame(animationFrameId);
      };
      // condition for shrinking the breadcrumb items
    }
  }, [dimensions, breadcrumbLength])

  //ellipsis dropdown close on click outside ellipsisDropdown
  useEffect(() => {
    if (hasEllipsis && showEllipsisDropdown) {
      const handleClickOutside = (event) => {
        if (showEllipsisDropdown && (ellipsisDropdown.current && !ellipsisDropdown.current.contains(event.target))) {
          setShowEllipsisDropdown(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      }
    }
  }, [showEllipsisDropdown])

  // ellipsis Dropdown
  const EllipsisDropdown = () => {
    if (isResponsive === 'ellipse') {
      for (let i = 0; i != breadcrumbLength - 1; i++) {
        if (i !== ellipsisIndex && breadcrumbLinkRef?.current[i]?.classList?.contains('isHidden')) {
          ellipsisItems.push(i);
        }
      }
    }

    if (isResponsive === 'ellipseFromLast') {
      return (<div className='Breadcrumb--v2__ellipsis-dropdown' ref={ellipsisDropdown} style={hiddenItems.length >= 5 ? { overflowY: 'scroll' } : {}}>
        {breadcrumbItems.map((breadcrumbItem, index) => (
          <div
            key={index}
            hidden={!hiddenItems.includes(index)}
            data-test-id={`cs-breadcrumb-ellipsis-item-${index}`}
          >
            <li onClick={() => breadcrumbItem.action ? breadcrumbItem.action() : undefined}>
              <div title={breadcrumbItem.name}>{breadcrumbItem.name}</div>
            </li>
          </div>
        ))}
      </div>)
    }

    return (
      <div className='Breadcrumb--v2__ellipsis-dropdown' ref={ellipsisDropdown} style={ellipsisItems.length >= 5 ? { overflowY: 'scroll' } : {}}>
        {breadcrumbItems.map((breadcrumbItem, index) => (
          <div
            key={index}
            hidden={!ellipsisItems.includes(index)}
            data-test-id={`cs-breadcrumb-ellipsis-item-${index}`}
          >
            <li onClick={() => breadcrumbItem.action ? breadcrumbItem.action() : undefined}>
              <div title={breadcrumbItem.name}>{breadcrumbItem.name}</div>
            </li>
          </div>
        ))}
      </div>
    );
  };

  const EllipseComponent = ({index, breadcrumbItem}: {index: number, breadcrumbItem: IBreadcrumbItemv2}) => {
    return (
      <div className={`Breadcrumb--v2__item`} key={index} tabIndex={0} style={{ position: 'relative' }} data-test-id={'cs-breadcrumb-ellipsis'}>
        <div className='Breadcrumb--v2__item--ellipse'>
          <div className='Breadcrumb--v2__item--text Breadcrumb--v2__item--ellipsis' onMouseDown={(e) => breadcrumbItem?.onDropdownClick ? breadcrumbItem.onDropdownClick() : toggleEllipsisDropdown(e)}><Icon version='v2' icon='KebabSmall' size='tiny' testId='cs-breadcrumb-kebab' />
            <Icon version='v2' icon='CaretDown' size='mini' />
          </div>
        </div>
        <Icon version='v2' icon='CaretRight' size='tiny' />

        {showEllipsisDropdown && (<EllipsisDropdown />)}
      </div>
    );
  }

  const DropDownComponent = ({dropdownItems, currenSelectedtItem}: {dropdownItems: any[], currenSelectedtItem: string}) => {
    return (
      <div className='Breadcrumb--v2__dropdown__items'>
        {dropdownItems.map((dropdownItem, index) => (
          <div
            key={index}
            data-test-id={`cs-dropdown-item-${index}`}
          >
            <li className={`${isEqual(dropdownItem.label, currenSelectedtItem) ? 'Breadcrumb--v2__dropdown__item--selected' : ''}`} onClick={() => dropdownItem.action ? dropdownItem.action() : undefined}>
              <div title={dropdownItem.label}>{dropdownItem.label}</div>
            </li>
          </div>
        ))}
      </div>
    );
  }

  // mainBreadcrumb
  return (
    <div className={`Breadcrumb--v2-main-wrapper ${breadcrumbLength > breadcrumbLimit ? 'Breadcrumb--v2-ellipsis' : ''}`} data-test-id={testId} ref={breadcrumbWrapperRef}>
      <div className={`Breadcrumb--v2`} ref={breadcrumbInnerRef}>
        {breadcrumbItems.map((breadcrumbItem, index) => {

          if (isEmpty(breadcrumbItem)) {
            return <></>
          }

          if (isResponsive === 'ellipseFromLast') {
            if (index === hiddenItems[0]) {
              return <EllipseComponent index={index} breadcrumbItem={breadcrumbItem} />
            }
          }

          // case for Ellipsis
          if (hasEllipsis && index === ellipsisIndex && isResponsive !== 'ellipseFromLast') {
            ellipsisItems.push(index);
            return (
              <div className={`Breadcrumb--v2__item`} key={index} tabIndex={0} style={{ position: 'relative' }} data-test-id={'cs-breadcrumb-ellipsis'}>
                <div className='Breadcrumb--v2__item--ellipse' ref={ellipsisRef}>
                  <div className='Breadcrumb--v2__item--text Breadcrumb--v2__item--ellipsis' onMouseDown={(e) => breadcrumbItem?.onDropdownClick ? breadcrumbItem.onDropdownClick() : toggleEllipsisDropdown(e)}><Icon version='v2' icon='KebabSmall' size='tiny' testId='cs-breadcrumb-kebab' />
                    <Icon version='v2' icon='CaretDown' size='mini' />
                  </div>
                </div>
                <Icon version='v2' icon='CaretRight' size='tiny' />

                {showEllipsisDropdown && (<EllipsisDropdown /> // Position the dropdown absolutely
                )}
              </div>
            )
          }
          // lets say the ellipse index is 3
          if (hasEllipsis && isResponsive == 'truncate' && index >= ellipsisIndex && index <= breadcrumbLength - breadcrumbLimit + ellipsisIndex) {
            ellipsisItems.push(index);
            return <></>;
          }

          if (hiddenItems.includes(index) && isResponsive === 'ellipseFromLast') {
            return <></>;
          }

          return (
            <>
              <div className={`Breadcrumb--v2__item ${breadcrumbItem.unLinkPresentItem && "Breadcrumb--v2__unlink-last-item"} ${breadcrumbItem.dropdownItems && 'Breadcrumb--v2__menu__dropdown'}`} key={index} tabIndex={0} ref={(el) => (breadcrumbLinkRef.current[index] = el)} data-test-id={`cs-breadcrumb-link-${index}`}
                onClick={() => (breadcrumbItem.action ? breadcrumbItem.action() : undefined)}>
                {(breadcrumbItem.icon || breadcrumbItem.name) && <div className='Breadcrumb--v2__item-content'>
                  {breadcrumbItem.icon && <div className="Breadcrumb--v2__item-icon"><Icon version={breadcrumbItem.icon.version || 'v2'} icon={breadcrumbItem.icon.name as string} size='tiny' /></div>}
                  {breadcrumbItem.name && (
                    <div
                      className={`Breadcrumb--v2__item--text`}
                      style={maxWidth ? { maxWidth: maxWidth } : {}}
                    >
                      <Truncate truncateFrom='end' maxChar={maxChar}>{breadcrumbItem.name}</Truncate>
                    </div>
                  )}
                </div>
                }
                {breadcrumbItem.dropdownItems && <div className='cs-breadcrumb--dropdown' id={`cs-breadcrumb-dropdown-${index}`}>
                  <DropDownComponent dropdownItems={breadcrumbItem.dropdownItems} currenSelectedtItem={breadcrumbItem.name} />
                  <Icon version='v2' size='medium' icon='DropDown' style={{ cursor: 'pointer' }} />
                </div>
                }
                {!breadcrumbItem.unLinkPresentItem && <Icon version='v2' icon='CaretRight' size='tiny' />}
              </div>
            </>
          )
        })}
        {presentLocation && <div className='Breadcrumb--v2__item' style={maxWidth ? { maxWidth: maxWidth } : {}} ref={(el) => breadcrumbLinkRef.current[breadcrumbLength] = el}><Truncate truncateFrom='end' maxChar={maxChar}>{presentLocation}</Truncate> </div>}
      </div>
    </div>
  )
}

const Breadcrumb = (props: IProps) => {

  // const { breadcrumbItems = [] } = props;
  const { breadcrumbItems = [], isResponsive = false, tooltipPosition = "bottom", maxWidth = 500, testId = "cs-breadcrumb", truncateLimit } = props;

  const totalItemsLength = breadcrumbItems.length;
  const [tooltipFlags, setTooltipFlags] = useState([]);
  const breadcrumbLinkRef: any = useRef([]);
  const breadcrumbWrapperRef: any = useRef([]);

  useEffect(() => {
    const offsetWidthLimitFlags = [];

    if (!isResponsive) {
      breadcrumbLinkRef?.current?.forEach(function (element: any) {
        offsetWidthLimitFlags.push(element?.offsetWidth >= maxWidth)
      })
      setTooltipFlags([...offsetWidthLimitFlags])
    }
  }, [breadcrumbItems])

  useEffect(() => {
    if (isResponsive) {
      const breadcrumbWrapper = breadcrumbWrapperRef?.current?.offsetWidth;
      const finalMaxWidth = `${(Math.floor((breadcrumbWrapper / breadcrumbItems.length)) - 20)}px`
      breadcrumbLinkRef?.current?.forEach(function (element: any) {
        element?.style?.setProperty('max-width', finalMaxWidth);
      })
    }
  }, [breadcrumbItems])

  function getBreadcrumbPath(breadcrumbArray) {
    const names = breadcrumbArray.map((item) => item.name);
    return names.join('/');
  }

  const breadcrumbPath = getBreadcrumbPath(breadcrumbItems);

  if (truncateLimit && breadcrumbItems.length >= truncateLimit) {
    const truncatedItems = breadcrumbItems.slice(0, 2).concat(breadcrumbItems.slice(-2));
    const ellipsis = <Tooltip content={breadcrumbPath} position={tooltipPosition}><span className='Breadcrumb__link--ellipses'>...&nbsp;/</span></Tooltip>;

    return (
      <div className="Breadcrumb" data-test-id={testId}>
        <span className='Breadcrumb__link flex-v-center'>
          {truncatedItems.map((item, index) => (
            <>
              {tooltipFlags?.length && tooltipFlags[index] ? (
                <Tooltip content={item.name} position={tooltipPosition}>
                  <span
                    onClick={item.action ? item.action : undefined}
                    ref={(el) => (breadcrumbLinkRef.current[index] = el)}
                    style={{ maxWidth: maxWidth }}
                    className="Breadcrumb__link--text"
                  >
                    {item.name}
                  </span>
                </Tooltip>
              ) : (
                <span
                  ref={(el) => (breadcrumbLinkRef.current[index] = el)}
                  className="Breadcrumb__link--text"
                  style={{ maxWidth: maxWidth }}
                >
                  {item.name}
                </span>
              )}
              {index < truncatedItems.length - 1 && ' / '}
              {index === 1 && ellipsis}
            </>
          ))}
        </span>
      </div>
    );
  }

  return (
    <div className="Breadcrumb" ref={breadcrumbWrapperRef} data-test-id={testId}>
      {breadcrumbItems.map((breadcrumbItem: IBreadcrumbItem, index: number) => {
        let isCurrentPage = index === totalItemsLength - 1

        if (isCurrentPage) {
          const name: any = breadcrumbItem.name
          return (
            <>
              {tooltipFlags?.length && tooltipFlags[index] ? (
                <Tooltip content={name} position={tooltipPosition}>
                  <span ref={el => breadcrumbLinkRef.current[index] = el} key={index} style={{ maxWidth: maxWidth }} className="Breadcrumb__link--text">{name}</span>
                </Tooltip>
              ) : (
                <>
                  {isResponsive && typeof name === 'string' ?
                    <span title={name} ref={el => breadcrumbLinkRef.current[index] = el} className="Breadcrumb__link--text" key={index} style={{ maxWidth: maxWidth }}>
                      {name}
                    </span> :
                    <span ref={el => breadcrumbLinkRef.current[index] = el} className="Breadcrumb__link--text" key={index} style={{ maxWidth: maxWidth }}>
                      {name}
                    </span>
                  }
                </>
              )}
            </>
          )
        }
        return (
          <>
            <span className="Breadcrumb__link flex-v-center" key={index}
              onClick={() => breadcrumbItem.action ? breadcrumbItem.action(breadcrumbItem) : undefined}
            >
              {tooltipFlags?.length && tooltipFlags[index] ? (
                <Tooltip content={breadcrumbItem.name} position={tooltipPosition}>
                  <span ref={el => breadcrumbLinkRef.current[index] = el} className="Breadcrumb__link--text " style={{ maxWidth: maxWidth }}>{`${breadcrumbItem.name}`}</span>
                </Tooltip>
              ) : (
                <>
                  {isResponsive && typeof breadcrumbItem.name === 'string' ?
                    <span title={breadcrumbItem.name} ref={el => breadcrumbLinkRef.current[index] = el} className="Breadcrumb__link--text" style={{ maxWidth: maxWidth }}>
                      {`${breadcrumbItem.name}`}
                    </span> :
                    <span ref={el => breadcrumbLinkRef.current[index] = el} className="Breadcrumb__link--text" style={{ maxWidth: maxWidth }}>
                      {`${breadcrumbItem.name}`}
                    </span>
                  }
                </>
              )
              }
              <span className="Breadcrumb__link--slash">{`/`}</span>
            </span>
          </>
        )
      })}
    </div>
  )
}

let withRouterBreadcrumb = withRouter(Breadcrumb)

const BreadcrumbComponent = (props: IProps | IPropsv2) => {
  if (props.version === 'v2') {
    return <Breadcrumbv2 {...props} />
  }
  return <Breadcrumb {...props} />
}


export default BreadcrumbComponent;
export { withRouterBreadcrumb, Breadcrumbv2 }

{/*
 <>
                  {isResponsive && typeof breadcrumbItem.name === 'string' ?
                    <span title={breadcrumbItem.name} ref={el => breadcrumbLinkRef.current[index] = el} className="Breadcrumb__link--text" style={{ maxWidth: props.maxWidth }}>
                      {`${breadcrumbItem.name}`}
                    </span> :
                    <span ref={el => breadcrumbLinkRef.current[index] = el} className="Breadcrumb__link--text" style={{ maxWidth: props.maxWidth }}>
                      {`${breadcrumbItem.name}`}
                    </span>
                  }
                </> 
              */}